import { useEffect, useRef, useState } from 'react';

import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';

import { useConfirmData } from '@alamo/api';
import { useAuth } from '@alamo/auth';
import { Button } from '@alamo/ui';
import { getApiErrorCode } from '@alamo/utils';

import type { ContractReconfirmationAccess } from '../../layouts/components/ConsultantContractGate';

import { DataReconfirmationModal } from './DataReconfirmationModal';

/** Consent and current contractual authorization precede personal reconfirmation. */
export function DataReconfirmationProvider({
  allowed,
  contractBlocked,
  refreshContractStatus,
}: ContractReconfirmationAccess): JSX.Element {
  const { t } = useTranslation('auth');
  const { user, refreshUser } = useAuth();
  const confirm = useConfirmData();
  const [dismissedUserId, setDismissedUserId] = useState<string>();
  const [contractRejected, setContractRejected] = useState(false);
  const [checkingStatus, setCheckingStatus] = useState(false);
  const observedBlockedStep = useRef(false);

  useEffect(() => {
    if (contractBlocked) observedBlockedStep.current = true;
    else if (allowed && observedBlockedStep.current) {
      setContractRejected(false);
      observedBlockedStep.current = false;
    }
  }, [allowed, contractBlocked]);

  const open =
    allowed &&
    Boolean(user?.requiresDataConfirmation) &&
    !user?.pending_reacceptance &&
    dismissedUserId !== user?.id &&
    !contractRejected;

  const handleConfirm = async (): Promise<void> => {
    if (!open || confirm.isPending) return;
    try {
      await confirm.mutateAsync();
      setDismissedUserId(user?.id);
      await refreshUser();
    } catch (error) {
      if (getApiErrorCode(error) === 'CONSULTANT_CONTRACT_REQUIRED') {
        setContractRejected(true);
        toast.error(t('profile.reconfirmation.contract_required'));
        await Promise.allSettled([refreshContractStatus(), refreshUser()]);
        return;
      }
      toast.error(t('profile.reconfirmation.error'));
    }
  };

  const recheckStatus = async (): Promise<void> => {
    if (checkingStatus) return;
    setCheckingStatus(true);
    try {
      if (await refreshContractStatus()) {
        await refreshUser();
        setContractRejected(false);
      }
    } catch {
      toast.error(t('profile.reconfirmation.status_error'));
    } finally {
      setCheckingStatus(false);
    }
  };

  return (
    <>
      {contractRejected && allowed && (
        <div
          role="alert"
          className="fixed bottom-4 left-4 right-4 z-50 ml-auto flex max-w-lg flex-wrap items-center gap-3 rounded-xl border border-slate-200 bg-alamo-surface p-4 font-body text-sm text-slate-600 shadow-lg"
        >
          <p className="min-w-0 flex-1">{t('profile.reconfirmation.contract_required')}</p>
          <Button size="sm" isLoading={checkingStatus} onClick={() => void recheckStatus()}>
            {t('profile.reconfirmation.check_status')}
          </Button>
        </div>
      )}
      {open && (
        <DataReconfirmationModal
          open
          onConfirm={() => void handleConfirm()}
          isConfirming={confirm.isPending}
        />
      )}
    </>
  );
}
